import { el, html, transitionsCompleted } from "@/functions";

export interface DialogButtonConfig {
  readonly name?: string;
  readonly type?: HTMLButtonElement["type"];
  readonly class?: string | readonly string[];
  readonly handler: (event: MouseEvent) => void;
}

export interface DialogOptions {
  /** Text to place in the title bar. */
  readonly title: string;
  /**
   * A mapping of button labels to objects that describe the button,
   * or a click handler for the button. Alternatively, provide the
   * preconfigured `HTMLButtonElement`.
   * */
  readonly buttons?:
    | Record<string, ((event: MouseEvent) => void) | DialogButtonConfig>
    | readonly HTMLButtonElement[];
  /** Do not render the default close button. */
  readonly noCloseButton?: boolean;
  /**
   * When true, the dialog is inserted in the DOM and opened on the microtask queue.
   * By default, inserting the dialog element and opening it must be managed by the
   * caller.
   */
  readonly autoOpen?: boolean;
}
/**
 * Creates a dialog element with the default look and feel for the app.
 *
 * It has built-in buttons on the bottom and a header with title at the top.
 *
 * The returned object contains the dialog reference as well as a content
 * element that can be configured with the dialog content.
 *
 * The dialog also has built-in behavior to remove itself from the DOM with
 * a nice animation when it is closed via `cancel` event or through the built-in
 * `close` button.
 * */
export function createDialog(
  titleOrOptions: string,
  dialogOptions?: Omit<DialogOptions, "title">,
): [dialog: HTMLDialogElement, content: HTMLElement];
export function createDialog(
  dialogOptions: DialogOptions,
): [dialog: HTMLDialogElement, content: HTMLElement];
export function createDialog(
  titleOrOptions: string | DialogOptions,
  dialogOptions?: Omit<DialogOptions, "title">,
): [dialog: HTMLDialogElement, content: HTMLElement] {
  // Normalize options
  dialogOptions ??= {};
  if (typeof titleOrOptions !== "string") {
    dialogOptions = titleOrOptions;
    titleOrOptions = titleOrOptions.title;
  }

  // Render shell
  const dialog = document.createElement("dialog");
  dialog.innerHTML = html`
    <header>
      <h2>${titleOrOptions}</h2>
    </header>

    <div class="dialog-content"></div>

    <div class="button-row">
      <button type="button" class="button" name="close">Close</button>
    </div>
  `;

  // Add event listeners:
  // Cancel should animate removal and then remove the dialog from the page
  // The close button should trigger cancel
  dialog.addEventListener("cancel", (event) => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      dialog.remove();
      return;
    }

    event.preventDefault();
    dialog.classList.add("removing");

    const onFinish = () => {
      dialog.remove();
      dialog.open = false;
      dialog.classList.remove("removing");
    };
    transitionsCompleted(dialog).finally(onFinish);
  });

  dialog.addEventListener("submit", (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.method !== "dialog") {
      return;
    }

    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    event.preventDefault();
    dialog.classList.add("removing");

    const onFinish = () => {
      dialog.remove();
      dialog.open = false;
      dialog.classList.remove("removing");
    };
    transitionsCompleted(dialog).finally(onFinish);
  });

  if (dialogOptions?.noCloseButton) {
    dialog.getElementsByTagName("button")[0].remove();
  } else {
    dialog.getElementsByTagName("button")[0].addEventListener("click", () => dialog.requestClose());
  }

  // Configure the buttons
  const buttonRow = dialog.children[dialog.children.length - 1];
  if (Array.isArray(dialogOptions.buttons)) {
    for (let i = dialogOptions.buttons.length - 1; i >= 0; i--) {
      const buttonDef = dialogOptions.buttons[i];
      buttonRow.prepend(buttonDef);
    }
  } else if (dialogOptions.buttons) {
    for (const [label, def] of Object.entries(dialogOptions.buttons).reverse()) {
      const normalDef: DialogButtonConfig = typeof def === "function" ? { handler: def } : def;

      const button = el("button");
      button.type = normalDef.type ?? "button";
      if (normalDef.name) {
        button.name = normalDef.name;
      }
      button.classList.add("button");
      if (typeof normalDef.class === "string") {
        button.classList.add(normalDef.class);
      } else if (Array.isArray(normalDef.class)) {
        button.classList.add(...normalDef.class);
      }
      button.addEventListener("click", normalDef.handler);
      button.append(label);

      buttonRow.prepend(button);
    }
  }

  if (dialogOptions?.autoOpen) {
    queueMicrotask(() => {
      document.body.append(dialog);
      dialog.showModal();
    });
  }

  // Return the dialog as well as its content
  // for the caller to configure the body
  return [dialog, dialog.querySelector("div.dialog-content")!];
}
